import {Box, Theme, useTheme} from '@mui/material';
import {useEffect, useId, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

type MermaidBlockProps = {
  chart: string;
  presentation?: 'agent';
};

export const getMermaidConfig = (theme: Theme, presentation?: 'agent') => {
  if (presentation !== 'agent') {
    return {
      startOnLoad: false,
      theme: theme.palette.mode === 'dark' ? ('dark' as const) : ('default' as const),
      fontFamily: theme.typography.fontFamily
    };
  }

  return {
    startOnLoad: false,
    securityLevel: 'strict' as const,
    theme: 'base' as const,
    fontFamily: theme.typography.fontFamily,
    themeVariables: {
      primaryColor: theme.palette.primary.light,
      primaryTextColor: theme.palette.getContrastText(theme.palette.primary.light),
      primaryBorderColor: theme.palette.primary.main,
      secondaryColor: theme.palette.secondary.light,
      secondaryTextColor: theme.palette.getContrastText(theme.palette.secondary.light),
      secondaryBorderColor: theme.palette.secondary.main,
      tertiaryColor: theme.palette.info.light,
      tertiaryTextColor: theme.palette.getContrastText(theme.palette.info.light),
      tertiaryBorderColor: theme.palette.info.main,
      textColor: theme.palette.text.primary,
      lineColor: theme.palette.divider,
      background: theme.palette.background.paper,
      clusterBkg: theme.palette.background.paper,
      clusterBorder: theme.palette.divider,
      edgeLabelBackground: theme.palette.background.paper
    }
  };
};

type MermaidRenderer = {
  initialize: (config: ReturnType<typeof getMermaidConfig>) => void;
  render: (id: string, chart: string) => Promise<{svg: string}>;
};

let mermaidRenderQueue: Promise<void> = Promise.resolve();

export const renderMermaidChart = (
  mermaid: MermaidRenderer,
  id: string,
  chart: string,
  config: ReturnType<typeof getMermaidConfig>
) => {
  const renderTask = mermaidRenderQueue.then(() => {
    mermaid.initialize(config);
    return mermaid.render(id, chart);
  });
  mermaidRenderQueue = renderTask.then(
    () => undefined,
    () => undefined
  );
  return renderTask;
};

const MermaidBlock = ({chart, presentation}: MermaidBlockProps) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const id = useId().replace(/:/g, '_');
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const {default: mermaid} = await import('mermaid');
        const {svg: renderedSvg} = await renderMermaidChart(
          mermaid,
          `mermaid-${id}`,
          chart.trim(),
          getMermaidConfig(theme, presentation)
        );
        const responsiveSvg = renderedSvg
          .replace(/(<svg[^>]*?)\s+width="[^"]*"/, '$1')
          .replace(/(<svg[^>]*?)\s+height="[^"]*"/, '$1');
        if (!cancelled) {
          setSvg(responsiveSvg);
          setError(null);
        }
      } catch {
        if (!cancelled) {
          setError(t('Mermaid図の描画に失敗しました'));
          setSvg('');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chart, id, isDark, presentation, theme, t]);

  if (error) {
    return (
      <Box
        component="pre"
        sx={{
          my: 0.5,
          p: 1,
          borderRadius: 1,
          bgcolor: isDark ? 'grey.900' : 'grey.200',
          overflow: 'auto',
          fontSize: theme.typography.caption.fontSize
        }}
      >
        <code>{chart}</code>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        my: 1,
        overflow: 'auto',
        textAlign: 'center',
        '& svg': {
          width: '100%',
          height: 'auto',
          display: 'block',
          margin: '0 auto'
        }
      }}
      dangerouslySetInnerHTML={{__html: svg}}
    />
  );
};

export default MermaidBlock;
